export interface AttributionData {
  fbclid: string | null;
  fbc: string | null;
  gclid: string | null;
  ttclid: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  referrer: string | null;
  landing_url: string | null;
  fbp: string | null;
}

const STORAGE_KEY = 'gc_attribution';

function readFbpCookie(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)_fbp=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function readFbcCookie(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)_fbc=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

export function captureAttribution(): AttributionData {
  const params = new URLSearchParams(window.location.search);

  const fbclid = params.get('fbclid');
  const fbc = fbclid ? `fb.1.${Date.now()}.${fbclid}` : readFbcCookie();

  const data: AttributionData = {
    fbclid,
    fbc,
    gclid: params.get('gclid'),
    ttclid: params.get('ttclid'),
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_content: params.get('utm_content'),
    referrer: document.referrer || null,
    landing_url: window.location.href,
    fbp: readFbpCookie(),
  };

  if (!getStoredAttribution()) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // localStorage blocked (private browsing, storage quota) — capture still returns data
    }
  }
  return data;
}

export function getStoredAttribution(): AttributionData | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearAttribution(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Attribution fields worth forwarding to PostHog as analytics attribution
 * (campaign + click IDs). The Meta-cookie values (fbc/fbp) are excluded HERE
 * because they're CAPI plumbing, not analytics — but they ARE set on the PostHog
 * person as Meta match keys at identify so the PostHog→Meta destination can match
 * conversions (see PostHogIdentify.tsx). */
const POSTHOG_ATTRIBUTION_FIELDS: (keyof AttributionData)[] = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'gclid',
  'fbclid',
  'ttclid',
  'referrer',
  'landing_url',
];

/**
 * Map captured attribution to PostHog property objects.
 *
 * The SPA boots and routes before PostHogProvider initializes posthog, so
 * posthog's own `$initial_utm_*` capture misses the ad-click query string
 * (verified: posthog records a bare `/onboarding` URL while this app's
 * captureAttribution still holds the UTMs). This bridges the reliably-captured
 * values into PostHog:
 *   - `superProps` → register() so every event carries the campaign params
 *     (event-level attribution; register does NOT create a person, so
 *     anonymous visitors never become PostHog/CIO profiles).
 *   - `setOnce`    → $set_once at identify so first-touch `initial_*` land on
 *     the person and are never overwritten by later sessions. Custom
 *     `initial_*` names avoid colliding with posthog's own (empty) `$initial_*`.
 * gc_attribution is itself first-touch (captureAttribution stores once), so
 * `initial_*` here is genuinely the first ad click.
 */
export function attributionToPosthogProps(a: AttributionData | null): {
  superProps: Record<string, string>;
  setOnce: Record<string, string>;
} {
  const superProps: Record<string, string> = {};
  const setOnce: Record<string, string> = {};
  if (!a) return { superProps, setOnce };

  for (const field of POSTHOG_ATTRIBUTION_FIELDS) {
    const value = a[field];
    if (typeof value === 'string' && value.length > 0) {
      superProps[field] = value;
      setOnce[`initial_${field}`] = value;
    }
  }
  return { superProps, setOnce };
}
